'use client'

import { useMemo } from 'react'
import { compareDesc } from 'date-fns'
import { ArrowRight } from '@carbon/icons-react'
import Link from 'next/link'

import { allBlogs } from 'contentlayer/generated'

import { truncateText, formatDate } from '@/lib/utils'
import { BlogThumb } from '@/components/blog-thumb'
import { Pagination } from './pagination'

interface PaginatedBlogPostsProps {
  page?: string
  tag?: string
  perPage?: number
}

/* Cards follow the suite-card anatomy used across the site: full-bleed
   image band with the shared grain pass, rounded-xl shell, padded body,
   tag row pinned to the bottom so it never drifts with excerpt length.
   Theme styling rides the tokens — no JS theme flag needed. */
export function PaginatedBlogPosts({
  page: pageParam,
  tag: tagParam,
  perPage = 6,
}: PaginatedBlogPostsProps) {
  const tag = tagParam
  const page = pageParam

  const currentPage = page ? parseInt(page, 10) : 1

  const sortedPosts = useMemo(() => {
    const posts = allBlogs.map((post) => ({
      ...post,
      cleanSlug: post.slugAsParams.split('/').slice(1).join('/'),
    }))

    return posts.sort((a, b) =>
      compareDesc(new Date(a.date || ''), new Date(b.date || ''))
    )
  }, [])

  const filteredPosts = useMemo(() => {
    if (!tag) return sortedPosts
    return sortedPosts.filter((post) => post.tags?.includes(tag))
  }, [sortedPosts, tag])

  const totalPages = Math.ceil(filteredPosts.length / perPage)
  const startIndex = (currentPage - 1) * perPage
  const endIndex = startIndex + perPage

  const currentPosts = filteredPosts.slice(startIndex, endIndex)

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 md:gap-6">
        {currentPosts.map((post) => {
          const href = `/blogs/${post.cleanSlug}`

          return (
            <Link
              key={post._id}
              href={href}
              className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-border/70 bg-background"
            >
              {/* Artwork band — full-bleed to the card edges. Generated from
                  the slug rather than an og_image: the frontmatter pointed at
                  stock photos hotlinked off a dozen third-party CDNs. */}
              <div className="relative h-[180px] w-full overflow-hidden md:h-[210px]">
                <BlogThumb seed={post.cleanSlug} />
                {/* Grain pass — one texture across every artwork on the site */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0"
                  style={{
                    backgroundImage: 'url(/gradients/grain.webp)',
                    backgroundRepeat: 'repeat',
                    backgroundSize: '512px 512px',
                    mixBlendMode: 'overlay',
                    opacity: 0.4,
                  }}
                />
              </div>

              {/* Body — padding on the text only */}
              <div className="flex flex-1 flex-col p-5 md:p-6">
                {post.date && (
                  <div className="mb-2 flex items-center gap-2">
                    <time
                      className="text-[11px] text-muted-foreground md:text-[12px]"
                      dateTime={post.date}
                    >
                      {formatDate(post.date, 'en-US')}
                    </time>
                    {post.readTimeInMinutes && (
                      <>
                        <span className="text-[11px] text-muted-foreground">
                          •
                        </span>
                        <span className="text-[11px] text-muted-foreground md:text-[12px]">
                          {post.readTimeInMinutes} min read
                        </span>
                      </>
                    )}
                  </div>
                )}

                <h2 className="font-heading mb-2 text-[18px] font-normal leading-[1.3] text-foreground md:text-[20px]">
                  {post.title}
                </h2>

                <p className="text-[13px] leading-[1.6] text-muted-foreground md:text-[14px]">
                  {truncateText(post.excerpt || '')}
                </p>

                {/* Pinned to the bottom so tags + CTA sit on one line across cards */}
                <div className="mt-auto flex flex-wrap gap-1.5 pt-4 md:gap-2">
                  {post.tags?.slice(0, 3).map((tag, idx) => (
                    <span
                      key={idx}
                      className="rounded-[4px] bg-emerald-600/10 px-2 py-0.5 text-[10px] font-medium text-foreground backdrop-blur-md dark:bg-primary-active/10 md:px-2.5 md:py-1 md:text-[11px]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <span className="inline-flex w-fit items-center gap-1.5 pt-6 text-[13px] font-medium text-foreground md:text-[14px]">
                  Learn more
                  <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          )
        })}
      </div>

      {totalPages > 1 && (
        <Pagination
          numberOfPages={totalPages}
          totalPages={totalPages}
          currentPage={currentPage}
          messages={{
            next: 'Next',
            previous: 'Previous',
            go_to_next_page: 'Go to next page',
            go_to_previous_page: 'Go to previous page',
          }}
        />
      )}
    </div>
  )
}
